<template>
    <div class="p-6 bg-gray-100 min-h-screen">
      <h1 class="text-2xl font-bold mb-6 text-gray-800">Запланированные уведомления</h1>

      <!-- Кнопка добавления уведомления -->
      <div class="mb-4">
        <button
          @click="showAddModal = true"
          class="bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-50"
        >
          Добавить уведомление
        </button>
      </div>

      <!-- Загрузка -->
      <div v-if="loading" class="text-center">
        <p class="text-gray-600">Загрузка уведомлений...</p>
      </div>

      <!-- Если уведомлений нет -->
      <div v-else-if="notifications.length === 0" class="text-center text-gray-500">
        <p>Уведомления не найдены.</p>
      </div>

      <!-- Таблица уведомлений -->
      <div v-else>
        <div class="overflow-x-auto">
          <table class="table-auto w-full bg-white shadow rounded-lg">
            <thead class="bg-gray-200 text-gray-600 uppercase text-sm font-semibold">
              <tr>
                <th class="py-3 px-6 text-left">Заголовок</th>
                <th class="py-3 px-6 text-left">Сообщение</th>
                <th class="py-3 px-6 text-left">Роли</th>
                <th class="py-3 px-6 text-left">Запланировано</th>
                <th class="py-3 px-6 text-center">Действия</th>
              </tr>
            </thead>
            <tbody class="text-gray-700">
              <tr
                v-for="notification in notifications"
                :key="notification.id"
                class="border-b border-gray-200 hover:bg-gray-100"
              >
                <td class="py-3 px-6 text-left font-medium">{{ notification.title }}</td>
                <td class="py-3 px-6 text-left">{{ notification.body }}</td>
                <td class="py-3 px-6 text-left">{{ notification.role.join(', ') }}</td>
                <td class="py-3 px-6 text-left">
                  {{ formatDate(notification.scheduled_at) }}
                </td>
                <td class="py-3 px-6 text-center">
                  <button
                    @click="handleDelete(notification.id)"
                    class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Модальное окно для добавления уведомления -->
      <div v-if="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
        <div class="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
          <h2 class="text-lg font-bold mb-4">Добавить уведомление</h2>
          <form @submit.prevent="handleAddNotification" class="space-y-4">
            <div>
              <label for="title" class="block text-sm font-medium text-gray-700">Заголовок</label>
              <input
                id="title"
                v-model="form.title"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                placeholder="Введите заголовок"
                required
              />
            </div>
            <div>
              <label for="body" class="block text-sm font-medium text-gray-700">Сообщение</label>
              <textarea
                id="body"
                v-model="form.body"
                rows="4"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                placeholder="Введите сообщение"
                required
              ></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Дата и время</label>
              <input
                type="datetime-local"
                v-model="form.scheduled_at"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Роли</label>
              <div class="space-y-2">
                <div v-for="role in roles" :key="role" class="flex items-center">
                  <input
                    type="checkbox"
                    :id="role"
                    :value="role"
                    v-model="form.role"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label :for="role" class="ml-2 text-sm text-gray-700">{{ role }}</label>
                </div>
              </div>
            </div>
            <div class="flex justify-end space-x-4">
              <button
                type="button"
                @click="showAddModal = false"
                class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                class="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-50"
              >
                Добавить
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>

  <script>
  import { fetchNotifications, deleteNotification, createNotification } from "@/services/notificationService";

  export default {
    data() {
      return {
        notifications: [],
        loading: true,
        showAddModal: false,
        form: {
          title: "",
          body: "",
          scheduled_at: null,
          role: [],
        },
        roles: ["Пациент", "Врач", "Медсестра"],
      };
    },
    methods: {
      async loadNotifications() {
        this.loading = true;
        try {
          this.notifications = await fetchNotifications();
        } catch (error) {
          console.error("Ошибка при загрузке уведомлений:", error);
        } finally {
          this.loading = false;
        }
      },
      async handleDelete(id) {
        if (confirm("Вы уверены, что хотите удалить это уведомление?")) {
          try {
            await deleteNotification(id);
            this.notifications = this.notifications.filter(
              (notification) => notification.id !== id
            );
          } catch (error) {
            console.error("Ошибка при удалении уведомления:", error);
          }
        }
      },
      async handleAddNotification() {
        try {
          await createNotification(this.form);
          alert("Уведомление успешно добавлено!");
          this.showAddModal = false;
          this.loadNotifications();
        } catch (error) {
          console.error("Ошибка при добавлении уведомления:", error);
          alert("Ошибка при добавлении уведомления.");
        }
      },
      formatDate(date) {
        return new Date(date).toLocaleString("ru-RU", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    mounted() {
      this.loadNotifications();
    },
  };
  </script>
